/**
 * <biosphere-product-gallery> — keeps the gallery in step with the picked variant.
 * See snippets/biosphere-product-gallery.liquid.
 *
 * Flagged: the root CLAUDE.md rules out variant-image machinery. Built on request and
 * deliberately the smallest version of it.
 *
 * It computes nothing and fetches nothing. Liquid renders every flavour's shot into
 * the panel and the first thumb, all but one hidden, and this only changes which is
 * hidden.
 *
 * That is also why the switch is instant. It used to re-render from the page the
 * picker fetches, which is correct but waits on a photo the browser has never
 * requested; the shots are now downloaded at low priority while the buyer reads.
 */
class BiosphereProductGallery extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();

    document.addEventListener(
      "biosphere:variant-picker:update",
      (event) => this.#swap(event),
      { signal: this.#abort.signal },
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * Reveal the picked flavour's shot. Every flavour's is already in the document,
   * downloaded at low priority behind the one on screen, so this is a visibility change
   * and not a request.
   *
   * The panel and the first thumb each hold the same set, so both are switched by one
   * pass over the same selector. A variant with no shot of its own leaves the current
   * one standing.
   */
  #swap(event) {
    // Another product's picker on the same page is not ours to answer.
    if (event.detail?.productId !== this.dataset.productId) return;

    const id = String(event.detail.variantId ?? "");
    const leads = [...this.querySelectorAll("[data-lead]")];
    if (!leads.some((lead) => lead.dataset.lead === id)) return;

    leads.forEach((lead) => {
      lead.hidden = lead.dataset.lead !== id;
    });
  }
}

if (!customElements.get("biosphere-product-gallery")) {
  customElements.define("biosphere-product-gallery", BiosphereProductGallery);
}
